import {
  FormStyle,
  TranslationWidth,
  getLocaleDayNames,
  getLocaleFirstDayOfWeek,
  getLocaleMonthNames,
} from '@angular/common';
import { ENVIRONMENT_INITIALIZER, Provider, effect, inject, untracked } from '@angular/core';

import { PrimeNG } from 'primeng/config';

import { TranslationStore } from './state/translation.store';

type Copy = Record<string, string>;

/**
 * PrimeNG's top-level texts (everything in its default translation except the date names, which
 * come from the locale data, and the filter-menu operators, which no screen uses). Covers the
 * select/multiselect messages ("{0} items selected", empty and search results), the datepicker
 * buttons and headers and the file upload copy.
 */
const MESSAGES: Record<string, Copy> = {
  es: {
    emptyMessage: 'No se han encontrado resultados',
    emptyFilterMessage: 'No se han encontrado resultados',
    emptySearchMessage: 'No se han encontrado resultados',
    emptySelectionMessage: 'Ningún elemento seleccionado',
    searchMessage: 'Hay resultados de búsqueda disponibles',
    selectionMessage: '{0} elementos seleccionados',
    noFilter: 'Sin filtro',
    clear: 'Limpiar',
    apply: 'Aplicar',
    accept: 'Sí',
    reject: 'No',
    choose: 'Elegir',
    upload: 'Subir',
    cancel: 'Cancelar',
    pending: 'Pendiente',
    completed: 'Completado',
    fileChosenMessage: 'Archivos',
    noFileChosenMessage: 'Ningún archivo seleccionado',
    chooseYear: 'Elegir año',
    chooseMonth: 'Elegir mes',
    chooseDate: 'Elegir fecha',
    prevDecade: 'Década anterior',
    nextDecade: 'Década siguiente',
    prevYear: 'Año anterior',
    nextYear: 'Año siguiente',
    prevMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    prevHour: 'Hora anterior',
    nextHour: 'Hora siguiente',
    prevMinute: 'Minuto anterior',
    nextMinute: 'Minuto siguiente',
    prevSecond: 'Segundo anterior',
    nextSecond: 'Segundo siguiente',
    today: 'Hoy',
    weekHeader: 'Sem',
    weak: 'Débil',
    medium: 'Media',
    strong: 'Fuerte',
    passwordPrompt: 'Introduce una contraseña',
  },
  en: {
    emptyMessage: 'No results found',
    emptyFilterMessage: 'No results found',
    emptySearchMessage: 'No results found',
    emptySelectionMessage: 'No selected item',
    searchMessage: 'Search results are available',
    selectionMessage: '{0} items selected',
    noFilter: 'No filter',
    clear: 'Clear',
    apply: 'Apply',
    accept: 'Yes',
    reject: 'No',
    choose: 'Choose',
    upload: 'Upload',
    cancel: 'Cancel',
    pending: 'Pending',
    completed: 'Completed',
    fileChosenMessage: 'Files',
    noFileChosenMessage: 'No file chosen',
    chooseYear: 'Choose year',
    chooseMonth: 'Choose month',
    chooseDate: 'Choose date',
    prevDecade: 'Previous decade',
    nextDecade: 'Next decade',
    prevYear: 'Previous year',
    nextYear: 'Next year',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    prevHour: 'Previous hour',
    nextHour: 'Next hour',
    prevMinute: 'Previous minute',
    nextMinute: 'Next minute',
    prevSecond: 'Previous second',
    nextSecond: 'Next second',
    today: 'Today',
    weekHeader: 'Wk',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    passwordPrompt: 'Enter a password',
  },
  de: {
    emptyMessage: 'Keine Ergebnisse gefunden',
    emptyFilterMessage: 'Keine Ergebnisse gefunden',
    emptySearchMessage: 'Keine Ergebnisse gefunden',
    emptySelectionMessage: 'Kein Element ausgewählt',
    searchMessage: 'Suchergebnisse sind verfügbar',
    selectionMessage: '{0} Elemente ausgewählt',
    noFilter: 'Kein Filter',
    clear: 'Leeren',
    apply: 'Anwenden',
    accept: 'Ja',
    reject: 'Nein',
    choose: 'Auswählen',
    upload: 'Hochladen',
    cancel: 'Abbrechen',
    pending: 'Ausstehend',
    completed: 'Abgeschlossen',
    fileChosenMessage: 'Dateien',
    noFileChosenMessage: 'Keine Datei ausgewählt',
    chooseYear: 'Jahr wählen',
    chooseMonth: 'Monat wählen',
    chooseDate: 'Datum wählen',
    prevDecade: 'Vorheriges Jahrzehnt',
    nextDecade: 'Nächstes Jahrzehnt',
    prevYear: 'Vorheriges Jahr',
    nextYear: 'Nächstes Jahr',
    prevMonth: 'Vorheriger Monat',
    nextMonth: 'Nächster Monat',
    prevHour: 'Vorherige Stunde',
    nextHour: 'Nächste Stunde',
    prevMinute: 'Vorherige Minute',
    nextMinute: 'Nächste Minute',
    prevSecond: 'Vorherige Sekunde',
    nextSecond: 'Nächste Sekunde',
    today: 'Heute',
    weekHeader: 'KW',
    weak: 'Schwach',
    medium: 'Mittel',
    strong: 'Stark',
    passwordPrompt: 'Passwort eingeben',
  },
  pt: {
    emptyMessage: 'Nenhum resultado encontrado',
    emptyFilterMessage: 'Nenhum resultado encontrado',
    emptySearchMessage: 'Nenhum resultado encontrado',
    emptySelectionMessage: 'Nenhum item selecionado',
    searchMessage: 'Há resultados de pesquisa disponíveis',
    selectionMessage: '{0} itens selecionados',
    noFilter: 'Sem filtro',
    clear: 'Limpar',
    apply: 'Aplicar',
    accept: 'Sim',
    reject: 'Não',
    choose: 'Escolher',
    upload: 'Carregar',
    cancel: 'Cancelar',
    pending: 'Pendente',
    completed: 'Concluído',
    fileChosenMessage: 'Ficheiros',
    noFileChosenMessage: 'Nenhum ficheiro selecionado',
    chooseYear: 'Escolher ano',
    chooseMonth: 'Escolher mês',
    chooseDate: 'Escolher data',
    prevDecade: 'Década anterior',
    nextDecade: 'Década seguinte',
    prevYear: 'Ano anterior',
    nextYear: 'Ano seguinte',
    prevMonth: 'Mês anterior',
    nextMonth: 'Mês seguinte',
    prevHour: 'Hora anterior',
    nextHour: 'Hora seguinte',
    prevMinute: 'Minuto anterior',
    nextMinute: 'Minuto seguinte',
    prevSecond: 'Segundo anterior',
    nextSecond: 'Segundo seguinte',
    today: 'Hoje',
    weekHeader: 'Sem',
    weak: 'Fraca',
    medium: 'Média',
    strong: 'Forte',
    passwordPrompt: 'Introduza uma palavra-passe',
  },
};

/**
 * PrimeNG's aria texts. setTranslation replaces the aria object as a whole, so every key PrimeNG
 * defines is listed here; a key missing from a language would otherwise vanish rather than fall
 * back. `dropdownTrigger` is ours: PrimeNG hardcodes "dropdown trigger" on the select button, and
 * selectAriaPassThrough reads this entry to label it.
 */
const ARIA: Record<string, Copy> = {
  es: {
    trueLabel: 'Verdadero',
    falseLabel: 'Falso',
    nullLabel: 'Sin seleccionar',
    star: '1 estrella',
    stars: '{star} estrellas',
    selectAll: 'Todos los elementos seleccionados',
    unselectAll: 'Ningún elemento seleccionado',
    close: 'Cerrar',
    previous: 'Anterior',
    next: 'Siguiente',
    navigation: 'Navegación',
    scrollTop: 'Volver arriba',
    moveTop: 'Mover al principio',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    moveBottom: 'Mover al final',
    moveToTarget: 'Mover al destino',
    moveToSource: 'Mover al origen',
    moveAllToTarget: 'Mover todo al destino',
    moveAllToSource: 'Mover todo al origen',
    pageLabel: 'Página {page}',
    firstPageLabel: 'Primera página',
    lastPageLabel: 'Última página',
    nextPageLabel: 'Página siguiente',
    prevPageLabel: 'Página anterior',
    previousPageLabel: 'Página anterior',
    rowsPerPageLabel: 'Filas por página',
    jumpToPageDropdownLabel: 'Ir a la página (desplegable)',
    jumpToPageInputLabel: 'Ir a la página',
    selectRow: 'Fila seleccionada',
    unselectRow: 'Fila no seleccionada',
    expandRow: 'Fila expandida',
    collapseRow: 'Fila contraída',
    showFilterMenu: 'Mostrar menú de filtro',
    hideFilterMenu: 'Ocultar menú de filtro',
    filterOperator: 'Operador de filtro',
    filterConstraint: 'Condición de filtro',
    editRow: 'Editar fila',
    saveEdit: 'Guardar edición',
    cancelEdit: 'Cancelar edición',
    listView: 'Vista de lista',
    gridView: 'Vista de cuadrícula',
    slide: 'Diapositiva',
    slideNumber: '{slideNumber}',
    zoomImage: 'Ampliar imagen',
    zoomIn: 'Acercar',
    zoomOut: 'Alejar',
    rotateRight: 'Girar a la derecha',
    rotateLeft: 'Girar a la izquierda',
    listLabel: 'Lista de opciones',
    selectColor: 'Elegir un color',
    removeLabel: 'Quitar',
    browseFiles: 'Buscar archivos',
    maximizeLabel: 'Maximizar',
    minimizeLabel: 'Minimizar',
    selectYear: 'Elegir año',
    selectMonth: 'Elegir mes',
    prevYear: 'Año anterior',
    nextYear: 'Año siguiente',
    prevMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    dropdownTrigger: 'Mostrar opciones',
  },
  en: {
    trueLabel: 'True',
    falseLabel: 'False',
    nullLabel: 'Not selected',
    star: '1 star',
    stars: '{star} stars',
    selectAll: 'All items selected',
    unselectAll: 'All items unselected',
    close: 'Close',
    previous: 'Previous',
    next: 'Next',
    navigation: 'Navigation',
    scrollTop: 'Scroll to top',
    moveTop: 'Move to top',
    moveUp: 'Move up',
    moveDown: 'Move down',
    moveBottom: 'Move to bottom',
    moveToTarget: 'Move to target',
    moveToSource: 'Move to source',
    moveAllToTarget: 'Move all to target',
    moveAllToSource: 'Move all to source',
    pageLabel: 'Page {page}',
    firstPageLabel: 'First page',
    lastPageLabel: 'Last page',
    nextPageLabel: 'Next page',
    prevPageLabel: 'Previous page',
    previousPageLabel: 'Previous page',
    rowsPerPageLabel: 'Rows per page',
    jumpToPageDropdownLabel: 'Jump to page dropdown',
    jumpToPageInputLabel: 'Jump to page',
    selectRow: 'Row selected',
    unselectRow: 'Row unselected',
    expandRow: 'Row expanded',
    collapseRow: 'Row collapsed',
    showFilterMenu: 'Show filter menu',
    hideFilterMenu: 'Hide filter menu',
    filterOperator: 'Filter operator',
    filterConstraint: 'Filter constraint',
    editRow: 'Edit row',
    saveEdit: 'Save edit',
    cancelEdit: 'Cancel edit',
    listView: 'List view',
    gridView: 'Grid view',
    slide: 'Slide',
    slideNumber: '{slideNumber}',
    zoomImage: 'Zoom image',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    rotateRight: 'Rotate right',
    rotateLeft: 'Rotate left',
    listLabel: 'Option list',
    selectColor: 'Select a color',
    removeLabel: 'Remove',
    browseFiles: 'Browse files',
    maximizeLabel: 'Maximize',
    minimizeLabel: 'Minimize',
    selectYear: 'Choose year',
    selectMonth: 'Choose month',
    prevYear: 'Previous year',
    nextYear: 'Next year',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    dropdownTrigger: 'Show options',
  },
  de: {
    trueLabel: 'Wahr',
    falseLabel: 'Falsch',
    nullLabel: 'Nicht ausgewählt',
    star: '1 Stern',
    stars: '{star} Sterne',
    selectAll: 'Alle Elemente ausgewählt',
    unselectAll: 'Keine Elemente ausgewählt',
    close: 'Schließen',
    previous: 'Zurück',
    next: 'Weiter',
    navigation: 'Navigation',
    scrollTop: 'Nach oben scrollen',
    moveTop: 'Ganz nach oben',
    moveUp: 'Nach oben',
    moveDown: 'Nach unten',
    moveBottom: 'Ganz nach unten',
    moveToTarget: 'Zum Ziel verschieben',
    moveToSource: 'Zur Quelle verschieben',
    moveAllToTarget: 'Alle zum Ziel verschieben',
    moveAllToSource: 'Alle zur Quelle verschieben',
    pageLabel: 'Seite {page}',
    firstPageLabel: 'Erste Seite',
    lastPageLabel: 'Letzte Seite',
    nextPageLabel: 'Nächste Seite',
    prevPageLabel: 'Vorherige Seite',
    previousPageLabel: 'Vorherige Seite',
    rowsPerPageLabel: 'Zeilen pro Seite',
    jumpToPageDropdownLabel: 'Seitenauswahl',
    jumpToPageInputLabel: 'Zu Seite springen',
    selectRow: 'Zeile ausgewählt',
    unselectRow: 'Zeile abgewählt',
    expandRow: 'Zeile erweitert',
    collapseRow: 'Zeile reduziert',
    showFilterMenu: 'Filtermenü anzeigen',
    hideFilterMenu: 'Filtermenü ausblenden',
    filterOperator: 'Filteroperator',
    filterConstraint: 'Filterbedingung',
    editRow: 'Zeile bearbeiten',
    saveEdit: 'Bearbeitung speichern',
    cancelEdit: 'Bearbeitung abbrechen',
    listView: 'Listenansicht',
    gridView: 'Rasteransicht',
    slide: 'Folie',
    slideNumber: '{slideNumber}',
    zoomImage: 'Bild vergrößern',
    zoomIn: 'Vergrößern',
    zoomOut: 'Verkleinern',
    rotateRight: 'Nach rechts drehen',
    rotateLeft: 'Nach links drehen',
    listLabel: 'Optionsliste',
    selectColor: 'Farbe wählen',
    removeLabel: 'Entfernen',
    browseFiles: 'Dateien durchsuchen',
    maximizeLabel: 'Maximieren',
    minimizeLabel: 'Minimieren',
    selectYear: 'Jahr wählen',
    selectMonth: 'Monat wählen',
    prevYear: 'Vorheriges Jahr',
    nextYear: 'Nächstes Jahr',
    prevMonth: 'Vorheriger Monat',
    nextMonth: 'Nächster Monat',
    dropdownTrigger: 'Optionen anzeigen',
  },
  pt: {
    trueLabel: 'Verdadeiro',
    falseLabel: 'Falso',
    nullLabel: 'Não selecionado',
    star: '1 estrela',
    stars: '{star} estrelas',
    selectAll: 'Todos os itens selecionados',
    unselectAll: 'Nenhum item selecionado',
    close: 'Fechar',
    previous: 'Anterior',
    next: 'Seguinte',
    navigation: 'Navegação',
    scrollTop: 'Voltar ao topo',
    moveTop: 'Mover para o início',
    moveUp: 'Mover para cima',
    moveDown: 'Mover para baixo',
    moveBottom: 'Mover para o fim',
    moveToTarget: 'Mover para o destino',
    moveToSource: 'Mover para a origem',
    moveAllToTarget: 'Mover tudo para o destino',
    moveAllToSource: 'Mover tudo para a origem',
    pageLabel: 'Página {page}',
    firstPageLabel: 'Primeira página',
    lastPageLabel: 'Última página',
    nextPageLabel: 'Página seguinte',
    prevPageLabel: 'Página anterior',
    previousPageLabel: 'Página anterior',
    rowsPerPageLabel: 'Linhas por página',
    jumpToPageDropdownLabel: 'Ir para a página (lista)',
    jumpToPageInputLabel: 'Ir para a página',
    selectRow: 'Linha selecionada',
    unselectRow: 'Linha não selecionada',
    expandRow: 'Linha expandida',
    collapseRow: 'Linha recolhida',
    showFilterMenu: 'Mostrar menu de filtro',
    hideFilterMenu: 'Ocultar menu de filtro',
    filterOperator: 'Operador de filtro',
    filterConstraint: 'Condição de filtro',
    editRow: 'Editar linha',
    saveEdit: 'Guardar edição',
    cancelEdit: 'Cancelar edição',
    listView: 'Vista de lista',
    gridView: 'Vista de grelha',
    slide: 'Diapositivo',
    slideNumber: '{slideNumber}',
    zoomImage: 'Ampliar imagem',
    zoomIn: 'Aproximar',
    zoomOut: 'Afastar',
    rotateRight: 'Rodar para a direita',
    rotateLeft: 'Rodar para a esquerda',
    listLabel: 'Lista de opções',
    selectColor: 'Escolher uma cor',
    removeLabel: 'Remover',
    browseFiles: 'Procurar ficheiros',
    maximizeLabel: 'Maximizar',
    minimizeLabel: 'Minimizar',
    selectYear: 'Escolher ano',
    selectMonth: 'Escolher mês',
    prevYear: 'Ano anterior',
    nextYear: 'Ano seguinte',
    prevMonth: 'Mês anterior',
    nextMonth: 'Mês seguinte',
    dropdownTrigger: 'Mostrar opções',
  },
};

/**
 * Builds the PrimeNG translation object for a locale from Angular's registered
 * locale data (month/day names, first weekday) plus the copy above, so selects,
 * multiselects, datepickers and paginators speak the app language instead of
 * PrimeNG's English defaults. A locale without copy above gets the English copy.
 */
export function primengTranslationFor(locale: string): Record<string, unknown> {
  const wide = FormStyle.Format;
  const lang = locale in MESSAGES ? locale : 'en';

  return {
    ...MESSAGES[lang],
    monthNames: [...getLocaleMonthNames(locale, wide, TranslationWidth.Wide)],
    monthNamesShort: [...getLocaleMonthNames(locale, wide, TranslationWidth.Abbreviated)],
    dayNames: [...getLocaleDayNames(locale, wide, TranslationWidth.Wide)],
    dayNamesShort: [...getLocaleDayNames(locale, wide, TranslationWidth.Abbreviated)],
    dayNamesMin: [...getLocaleDayNames(locale, wide, TranslationWidth.Narrow)],
    firstDayOfWeek: getLocaleFirstDayOfWeek(locale),
    aria: { ...ARIA[lang] },
  };
}

/** The params PrimeNG hands a function passthrough section; only the multiselect's context is read. */
export interface PassThroughParams {
  context?: { selected?: boolean };
}

export type PassThroughSection =
  | Record<string, unknown>
  | ((params?: PassThroughParams) => Record<string, unknown>);

type PassThrough = Record<string, Record<string, PassThroughSection | undefined> | undefined>;

/**
 * The global passthrough with the select aria labels PrimeNG gets wrong set in the locale's
 * language, on every p-select and p-multiselect, wrapped or not:
 * - the p-select trigger, where PrimeNG writes a literal aria-label="dropdown trigger" and offers
 *   no translation key;
 * - the p-multiselect option list and select-all checkbox, where PrimeNG binds its listLabel and
 *   toggleAllAriaLabel computeds without calling them, so the labels read "[Computed …]". The
 *   checkbox section is a function because its label follows the select-all state, which PrimeNG
 *   passes in as context.selected.
 * Passthrough attributes are applied after the template's own, so they replace them. Any other
 * global passthrough is kept.
 */
export function selectAriaPassThrough(pt: unknown, locale: string): PassThrough {
  const current = (pt ?? {}) as PassThrough;
  const select = current['select'] ?? {};
  const multiselect = current['multiselect'] ?? {};
  const aria = ARIA[locale] ?? ARIA['en'];
  const section = (value: unknown, params?: PassThroughParams): Record<string, unknown> =>
    ((typeof value === 'function' ? value(params) : value) ?? {}) as Record<string, unknown>;

  return {
    ...current,
    select: {
      ...select,
      dropdown: { ...section(select['dropdown']), 'aria-label': aria['dropdownTrigger'] },
    },
    multiselect: {
      ...multiselect,
      list: { ...section(multiselect['list']), 'aria-label': aria['listLabel'] },
      pcHeaderCheckbox: (params?: PassThroughParams) => {
        const checkbox = section(multiselect['pcHeaderCheckbox'], params);
        return {
          ...checkbox,
          input: {
            ...section(checkbox['input']),
            'aria-label': aria[params?.context?.selected ? 'selectAll' : 'unselectAll'],
          },
        };
      },
    },
  };
}

export interface PrimeNgLocaleOptions {
  /**
   * The app's own date names and first weekday for a language, applied over the locale-data ones
   * in the same setTranslation call, so no other effect has to write them (two writers would race
   * on which runs last).
   */
  dateNames?: (lang: string) => Record<string, unknown>;
}

/**
 * Keeps PrimeNG's translation in sync with the active language: applies it at
 * bootstrap (the SSR case, where the locale is frozen per request) and again on
 * every runtime language switch. It is the only writer of PrimeNG's translation.
 */
export function providePrimeNgLocale(options: PrimeNgLocaleOptions = {}): Provider {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue: () => {
      const primeng = inject(PrimeNG);
      const store = inject(TranslationStore);
      // The app's own passthrough, so each language switch builds on it rather than on the last one.
      // providePrimeNG sets it in an app initializer, after this one, so it is read on the first run.
      // The passthrough is tracked too: anything that replaces it later (at any time, not only before
      // a language switch) becomes the new base and gets the select aria labels again at once.
      let appPassThrough: unknown;
      let applied: unknown;
      let appliedLang: string | undefined;
      effect(() => {
        const lang = store.activeLanguage();
        const current = primeng.pt();
        untracked(() => {
          // The rerun our own pt.set below triggers: nothing changed since.
          if (current === applied && lang === appliedLang) {
            return;
          }
          if (current !== applied) {
            appPassThrough = current;
          }
          if (lang !== appliedLang) {
            primeng.setTranslation({
              ...primengTranslationFor(lang),
              ...options.dateNames?.(lang),
            });
            appliedLang = lang;
          }
          applied = selectAriaPassThrough(appPassThrough, lang);
          primeng.pt.set(applied as never);
        });
      });
    },
  };
}
